import React, { useEffect, useState, useMemo, useCallback } from 'react';
import MainMenu from "../../components/menu/Menu";
import { ConfigProvider, Input, Layout, Menu, MenuProps, Space, Table } from "antd";
import './style.css'
import ButtonAddPlus from "../../components/buttons/buttonAddPlus/ButtonAddPlus";
import ButtonAddPlus2 from "../../components/buttons/buttonAddPlus2/ButtonAddPlus2";
import ButtonFilterRS from "../../components/buttons/buttonFilter2/ButtonFilterRS";
import { SearchOutlined } from "@ant-design/icons";
import { useUserStore } from "../../store/users/UserStore";
import IconLeftMenuFooter from "../../components/icons/iconLeftMenu/IconLeftMenuFooter";
import { useSelectedRowKeys2 } from "../../store/devices/useSelectedRowKeys2";
import { User } from "../../types/User";
import ButtonLeftMenuFooterDelete from "../../components/buttons/buttonLeftMenu/ButtonLeftMenuFooterDelete";
import ButtonLeftMenuFooterEdit from "../../components/buttons/buttonLeftMenu/ButtonLeftMenuFooterEdit";
import { useMenuUsersState } from "../../store/users/useMenuUsersState";
import { useOwnerStore } from "../../store/owner/useOwnerStore";
import { Owner } from "../../types/Owner";
import NewUserModal from "../../components/modals/userModals/newUser/NewUserModal";
import DeleteUserModal from "../../components/modals/userModals/GroupDeleteUser/DeleteUser";
import UpdateUserModal from "../../components/modals/userModals/EditUser/UpdateUserModal";
import GroupEditUserModal from "../../components/modals/userModals/GroupEditUser/GroupEditUser";
import NewOwnerModal from "../../components/modals/userModals/newOwner/NewOwnerModal";
import { useGroupsStore } from '../../store/groups/Groups';
import { Group } from '../../types/Group';
import Tag from "../../utils/shared/components/Tags/Tag";
import { useAuthStore } from '../../store/auth/auth';
import DeleteOwnerModal from "../../components/modals/userModals/GroupDeleteOwner/DeleteOwner";
import FilteredUserModal from '../../components/modals/userModals/FilteredUser/FilteredUserModal';
import FilteredOwnerModal from '../../components/modals/userModals/FilteredOwner/FilteredOwnerModal';
import { getInitials, getGroupNames, getRoleLabel } from '../../utils/userUtils';
import UsersTable from '../../components/tables/tableUsers/UsersTable';
import OwnersTable from '../../components/tables/tableUsers/OwnersTable';
import useFilteredUsers from '../../hooks/useFilteredUsers';
import useFilteredOwners from '../../hooks/useFilteredOwners';
import useUserModals from '../../hooks/useUserModals';
import { ModalTypeUser } from '../../types/ModalTypeUser';

const { Header, Content, Footer } = Layout;

const Users: React.FC = () => {
    const [currentMenuItem, setCurrentMenuItem] = useState('users');
    const [searchText, setSearchText] = useState('');
    const { users, fetchUsers } = useUserStore();
    const { owners, fetchOwners } = useOwnerStore();
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const { selectedRowKeys2, setSelectedRowKeys2 } = useSelectedRowKeys2();
    const { selectedStateMenuUsers, setSelectedStateMenuUsers } = useMenuUsersState();
    const {
        activeModal,
        setActiveModal,
        handleModalOk,
        handleModalCancel,
        handleEditAction,
        handleDeleteAction,
        handleUpdateAction,
        setSelectedUser,
        selectedUser
    } = useUserModals({
        fetchUsers,
        fetchOwners,
        selectedStateMenuUsers,
        setSelectedRowKeys2,
        users
    });

    const { groups, fetchGroups } = useGroupsStore();
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const { user } = useAuthStore();

    const allGroups = useMemo(() => {
        const getAllSubGroups = (group: Group): Group[] => {
            const subGroups: Group[] = [];
            group.sub_groups.forEach((subGroup: Group) => {
                subGroups.push(subGroup);
                if (subGroup.sub_groups) {
                    subGroups.push(...getAllSubGroups(subGroup));
                }
            });
            return subGroups;
        };
        const result: Group[] = [];
        groups.forEach((group: Group) => {
            result.push(group);
            if (group.sub_groups) {
                result.push(...getAllSubGroups(group));
            }
        });
        return result;
    }, [groups]);

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [isFilterOwnerDrawerOpen, setIsFilterOwnerDrawerOpen] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

    const filteredUsers = useFilteredUsers(users, searchText, selectedRoles, selectedGroups, allGroups);
    const filteredOwners = useFilteredOwners(owners, searchText, selectedTypes);

    const columns = useMemo(() => [
        {
            title: 'Пользователь',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: User) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="user-initials-users">{getInitials(record.name)}</span>
                    {text}
                </span>
            ),
            width: 680,
        },
        {
            title: 'Логин',
            dataIndex: 'login',
            key: 'login',
            width: 240,
        },
        {
            title: 'Группа',
            dataIndex: 'groups',
            key: 'groups',
            width: 680,
            render: (groups: (string | { uid: string })[]) => getGroupNames(groups, allGroups),
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            width: 240,
            render: (_: unknown, record: User) => (
                <Tag state={record.isAdmin ? "processing" : "default"} border={true}>
                    {record.isAdmin ? "Администратор" : "Оператор"}
                </Tag>
            ),
        },
    ], [getGroupNames, allGroups]);

    const columns2 = useMemo(() => [
        {
            title: 'ФИО сотрудника / Гос. номер машины',
            dataIndex: 'name',
            key: 'name',
            width: 920,
            render: (text: string, record: Owner) => {
                const initialsClass = record.car ? "user-initials-car" : "user-initials-users";
                return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className={initialsClass}>{getInitials(text)}</span>
                        {text}
                    </span>
                );
            },
        },
        {
            title: 'Тип',
            key: 'type',
            width: 920,
            render: (_: any, record: Owner) => (
                <Tag state={record.car ? "error" : "success"} border={true}>
                    {record.car ? "Машина" : "Сотрудник"}
                </Tag>
            ),
        },
    ], []);

    useEffect(() => {
        fetchUsers();
        fetchGroups();
        fetchOwners(1, 10000);
    }, [fetchUsers, fetchGroups, fetchOwners]);

    const onClick: MenuProps['onClick'] = useCallback((e: unknown) => {
        const event = e as { key: string };
        setSelectedStateMenuUsers(event.key);
        setSelectedRowKeys2([]);
    }, [setSelectedStateMenuUsers, setSelectedRowKeys2]);

    useEffect(() => {
        setSelectedRowKeys2([]);
    }, [selectedStateMenuUsers, setSelectedRowKeys2]);

    const handleMenuClick = useCallback((key: string) => {
        setCurrentMenuItem(key);
    }, []);

    const handleFilter = useCallback(() => {
        if (selectedStateMenuUsers === 'users') {
            setIsFilterDrawerOpen(true);
        } else if (selectedStateMenuUsers === 'owners') {
            setIsFilterOwnerDrawerOpen(true);
        }
    }, [selectedStateMenuUsers]);

    const handleFilterDrawerClose = useCallback(() => {
        setIsFilterDrawerOpen(false);
    }, []);

    const handleFilterOwnerDrawerClose = useCallback(() => {
        setIsFilterOwnerDrawerOpen(false);
    }, []);

    const handleRoleChange = useCallback((roles: string[]) => {
        setSelectedRoles(roles);
        setSelectedRowKeys2([]);
    }, [setSelectedRowKeys2]);

    const handleGroupChange = useCallback((groups: string[]) => {
        setSelectedGroups(groups);
        setSelectedRowKeys2([]);
    }, [setSelectedRowKeys2]);

    const handleTypeChange = useCallback((types: string[]) => {
        setSelectedTypes(types);
        setSelectedRowKeys2([]);
    }, [setSelectedRowKeys2]);

    const availableRoles = useMemo(() => [
        { label: 'Администратор', value: 'администратор' },
        { label: 'Оператор', value: 'оператор' },
    ], []);

    const availableGroups = useMemo(() =>
        allGroups
            .filter(g => g.name !== 'Несортированные' && g.name !== 'Все устройства')
            .map(g => ({ label: g.name, value: g.uid })),
        [allGroups]);

    const availableTypes = useMemo(() => [
        { label: 'Сотрудник', value: 'сотрудник' },
        { label: 'Машина', value: 'машина' },
    ], []);

    const onSelectChange = useCallback((newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys2(newSelectedRowKeys);
    }, [setSelectedRowKeys2]);

    const handleRowClick = useCallback((record: User | Owner) => {
        if ('isAdmin' in record) {
            if (user?.isAdmin) {
                setSelectedUser(record);
                setActiveModal(ModalTypeUser.UPDATE_USER);
            }
        } else {
            // Это Owner
        }
    }, [user, setSelectedUser, setActiveModal]);

    const rowSelection = useMemo(() => ({
        selectedRowKeys: selectedRowKeys2,
        onChange: onSelectChange,
    }), [selectedRowKeys2, onSelectChange]);

    const items: MenuProps['items'] = useMemo(() => [
        {
            label: (<div className="Users">
                <h1 className="name">Сотрудники</h1>
                <h1 className="count">({(users?.length ?? 0)})</h1>
                <ButtonAddPlus onClick={() => {
                    setSelectedStateMenuUsers('users');
                    setActiveModal(ModalTypeUser.ADD_USER);
                }} />
            </div>),
            key: 'users'
        },
        {
            label: (<div className="Owners">
                <h1 className="name">Владельцы устройств</h1>
                <h1 className="count">({(owners?.length ?? 0)})</h1>
                <ButtonAddPlus2 onClick={() => {
                    setSelectedStateMenuUsers('owners');
                    setActiveModal(ModalTypeUser.ADD_OWNER);
                }} />
            </div>),
            key: 'owners',
        }
    ], [users?.length, owners?.length, setSelectedStateMenuUsers, setActiveModal]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    }, []);

    const handleClearSelection = useCallback(() => {
        setSelectedRowKeys2([]);
    }, [setSelectedRowKeys2]);

    const selectedOwners = useMemo(() => {
        return (owners ?? []).filter(o => selectedRowKeys2.includes(o.UID)).map(o => ({ UID: o.UID }));
    }, [owners, selectedRowKeys2]);

    return (
        <Layout style={{ height: '100vh' }}>
            <Header style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 0,
                paddingRight: 0
            }}>
                <div className="menu">
                    <MainMenu onClick={handleMenuClick} currentMenuItem={currentMenuItem} />
                </div>
            </Header>
            <Layout>
                <Content style={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#ffffff',
                }}>
                    <div className="headerTable">
                        <ConfigProvider
                            theme={{
                                components: {
                                    Menu: {
                                        itemPaddingInline: 0,
                                        horizontalItemSelectedColor: '#FCE49C',
                                        activeBarHeight: 4
                                    },
                                },
                            }}
                        >
                            <div className="left_HT">
                                <Menu className="menuLine" onClick={onClick} selectedKeys={[selectedStateMenuUsers]} mode="horizontal"
                                    items={items} />
                            </div>
                        </ConfigProvider>
                        <div className="right_HT">
                            <Input
                                placeholder={"Поиск"}
                                className="right_HT_input"
                                suffix={<SearchOutlined style={{ marginLeft: '0px', padding: 0 }} />}
                                value={searchText}
                                onChange={handleSearchChange}
                            />
                            <ButtonFilterRS onClick={handleFilter} />
                        </div>
                    </div>
                    {selectedStateMenuUsers === "users" ? (
                        <div style={{ flex: 1, overflowY: 'auto', margin: '24px', marginTop: '16px', marginBottom: selectedRowKeys2.length > 0 ? '0' : '24px' }}>
                            <div className="table_container">
                                <UsersTable
                                    users={filteredUsers}
                                    allGroups={allGroups}
                                    selectedRowKeys={selectedRowKeys2}
                                    onSelectChange={onSelectChange}
                                    onRowClick={handleRowClick}
                                />
                            </div>
                        </div>
                    ) : (
                        <div style={{ flex: 1, overflowY: 'auto', margin: '24px', marginTop: '16px', marginBottom: selectedRowKeys2.length > 0 ? '0' : '24px' }}>
                            <div className="table_container">
                                <OwnersTable
                                    owners={filteredOwners}
                                    selectedRowKeys={selectedRowKeys2}
                                    onSelectChange={onSelectChange}
                                    onRowClick={handleRowClick}
                                />
                            </div>
                        </div>
                    )}
                </Content>
                <Footer style={{
                    display: (selectedRowKeys2.length > 0 ? 'initial' : 'none'),
                    alignItems: 'center',
                    paddingLeft: 24,
                    paddingRight: 0,
                    background: 'white',
                    position: 'sticky',
                    bottom: 0,
                    height: 56,
                    padding: 0,
                    marginBottom: "24px",
                    marginLeft: "24px",
                    marginRight: "24px"
                }}>
                    <div className="tableFooter">
                        <span className="countDeviceFooter">
                            <button
                                className="clear-selection-btn"
                                onClick={handleClearSelection}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    margin: 0,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <IconLeftMenuFooter />
                            </button>
                            <h3 className="h3Footer">Выбрано: {selectedRowKeys2.length}</h3>
                        </span>
                        <div>
                            {/* <ButtonLeftMenuFooterEdit onClick={handleEditAction} /> */}
                            <ButtonLeftMenuFooterDelete onClick={handleDeleteAction} />
                        </div>
                    </div>
                </Footer>
            </Layout>
            <NewUserModal open={activeModal === ModalTypeUser.ADD_USER} onOk={handleModalOk} onCancel={handleModalCancel} />
            <NewOwnerModal open={activeModal === ModalTypeUser.ADD_OWNER} onOk={handleModalOk} onCancel={handleModalCancel} />
            <DeleteUserModal open={activeModal === ModalTypeUser.DELETE_USER && selectedStateMenuUsers === 'users'} users={selectedRowKeys2} onOk={handleModalOk} onCancel={handleModalCancel} />
            <DeleteOwnerModal open={activeModal === ModalTypeUser.DELETE_USER && selectedStateMenuUsers === 'owners'} owners={selectedOwners} onOk={handleModalOk} onCancel={handleModalCancel} />
            <UpdateUserModal open={activeModal === ModalTypeUser.UPDATE_USER} onCancel={handleModalCancel} onOk={handleModalOk} selectedUser={selectedUser} />
            <GroupEditUserModal
                open={activeModal === ModalTypeUser.EDIT_USER}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                device={selectedRowKeys2}
                selectedUser={selectedRowKeys2.length === 1 ? users.find(u => u.uid === selectedRowKeys2[0]) : undefined}
            />
            <FilteredUserModal
                visible={isFilterDrawerOpen}
                onClose={handleFilterDrawerClose}
                selectedRoles={selectedRoles}
                selectedGroups={selectedGroups}
                onRoleChange={handleRoleChange}
                onGroupChange={handleGroupChange}
                availableRoles={availableRoles}
                availableGroups={availableGroups}
            />
            <FilteredOwnerModal
                visible={isFilterOwnerDrawerOpen}
                onClose={handleFilterOwnerDrawerClose}
                selectedTypes={selectedTypes}
                onTypeChange={handleTypeChange}
                availableTypes={availableTypes}
            />
        </Layout>
    );
};

export default Users;
